import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'

import { api, can, isActive, type Integration, type IntegrationInput } from '../api'
import { ErrorBox, Pill, RunStatus } from '../components'
import { dt } from '../format'

// Integrations are connections an admin sets up and enables (an HTTP API or
// a Google Drive service account). Sources pick a path or file under one.
// Credentials are write-only: they are encrypted to the organisation's key
// on save, and only the worker can decrypt them (so "test connection" is a
// run). The API returns a hint, never the secret.

export function Integrations() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({ queryKey: ['integrations'], queryFn: api.integrations })
  const [creating, setCreating] = useState(false)
  const admin = can(me.data, 'admin')
  const done = () => qc.invalidateQueries({ queryKey: ['integrations'] })

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Integrations</h1>
          <p className="sub">Connections that automated extractions fetch through. Only admins set them up and enable them.</p>
        </div>
        {admin && (
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New integration'}
          </button>
        )}
      </header>
      {q.error && <ErrorBox error={q.error} />}
      {q.data && (
        <p className="note">
          {q.data.sealing_enabled ? (
            <>Credentials are encrypted to the organisation’s key when saved; only the worker can decrypt them (org keys wrapped by <code>{q.data.kek}</code>).</>
          ) : (
            <strong>Credentials cannot be stored on this installation: no key-encryption key is configured.</strong>
          )}
        </p>
      )}
      {creating && (
        <IntegrationForm
          onDone={() => {
            setCreating(false)
            done()
          }}
        />
      )}
      {q.data?.integrations.length === 0 && !creating && <p className="muted">No integrations yet.</p>}
      {q.data?.integrations.map((it) => <IntegrationCard key={it.slug} it={it} admin={admin} onChange={done} />)}
    </section>
  )
}

function IntegrationCard({ it, admin, onChange }: { it: Integration; admin: boolean; onChange: () => void }) {
  const [editing, setEditing] = useState(false)
  const toggle = useMutation({ mutationFn: () => api.setIntegrationEnabled(it.slug, !it.enabled), onSuccess: onChange })
  const test = useMutation({ mutationFn: () => api.testIntegration(it.slug) })
  // The test runs in the worker; follow its run.
  const testRun = useQuery({
    queryKey: ['run', test.data?.id],
    queryFn: () => api.run(test.data!.id),
    enabled: !!test.data,
    refetchInterval: (q) => (q.state.data && !isActive(q.state.data) ? false : 1500),
  })
  const testing = test.isPending || isActive(testRun.data)
  const del = useMutation({ mutationFn: () => api.deleteIntegration(it.slug), onSuccess: onChange })
  return (
    <div className="card">
      <div className="row-between">
        <h2>
          {it.name} <span className="muted mono">{it.slug}</span>
        </h2>
        <div className="row">
          <Pill kind={it.enabled ? 'ok' : 'warn'}>{it.enabled ? 'enabled' : 'disabled'}</Pill>
          {admin && (
            <>
              <button className="btn" disabled={testing} onClick={() => test.mutate()}>
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              <button className="btn" disabled={toggle.isPending} onClick={() => toggle.mutate()}>
                {it.enabled ? 'Disable' : 'Enable'}
              </button>
              <button className="btn" onClick={() => setEditing((v) => !v)}>
                {editing ? 'Cancel' : 'Edit'}
              </button>
              {it.sources.length === 0 && (
                <button className="btn" disabled={del.isPending} onClick={() => del.mutate()}>
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {testRun.data && isActive(testRun.data) && <p className="note">Testing in the worker: <RunStatus run={testRun.data} /></p>}
      {testRun.data && !isActive(testRun.data) && (
        <p className={`callout ${testRun.data.status === 'succeeded' ? 'ok' : 'err'}`}>
          {testRun.data.status === 'succeeded' ? 'The connection works.' : testRun.data.error ?? testRun.data.status}
        </p>
      )}
      {[toggle.error, test.error, del.error].map((e, i) => e && <ErrorBox key={i} error={e} />)}
      <dl className="kv">
        <dt>Kind</dt>
        <dd className="mono">{it.kind}</dd>
        {it.kind === 'http' ? (
          <>
            <dt>Base URL</dt>
            <dd className="mono">{String(it.config.base_url ?? '')}</dd>
            <dt>Auth</dt>
            <dd className="mono">
              {String(it.config.auth ?? 'none')}
              {it.config.auth_name ? ` (${String(it.config.auth_name)})` : ''}
              {it.config.username ? ` user ${String(it.config.username)}` : ''}
            </dd>
          </>
        ) : (
          <>
            <dt>Share files with</dt>
            <dd className="mono">{String(it.config.client_email ?? 'upload the service-account key')}</dd>
          </>
        )}
        <dt>Credential</dt>
        <dd>{it.secret_hint || <span className="muted">none</span>}</dd>
        <dt>Enabled</dt>
        <dd>{it.enabled_at ? `${dt(it.enabled_at)} by ${it.enabled_by}` : '–'}</dd>
        <dt>Sources</dt>
        <dd>
          {it.sources.length === 0
            ? '–'
            : it.sources.map((s, i) => (
                <span key={s}>
                  {i > 0 && ', '}
                  <Link to={`/sources/${s}`}>{s}</Link>
                </span>
              ))}
        </dd>
      </dl>
      {it.description && <p className="note">{it.description}</p>}
      {editing && (
        <IntegrationForm
          initial={it}
          onDone={() => {
            setEditing(false)
            onChange()
          }}
        />
      )}
    </div>
  )
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

function IntegrationForm({ initial, onDone }: { initial?: Integration; onDone: () => void }) {
  const c = initial?.config ?? {}
  const headersText = (h: unknown) =>
    Object.entries((h as Record<string, string>) ?? {})
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n')
  const [f, setF] = useState({
    name: initial?.name ?? '',
    slug: initial?.slug ?? '',
    kind: initial?.kind ?? ('http' as 'http' | 'google_drive'),
    description: initial?.description ?? '',
    base_url: String(c.base_url ?? 'https://'),
    auth: String(c.auth ?? 'none'),
    auth_name: String(c.auth_name ?? ''),
    username: String(c.username ?? ''),
    headers: headersText(c.headers),
    test_path: String(c.test_path ?? ''),
    test_body: String(c.test_body ?? ''),
    body_content_type: String(c.body_content_type ?? 'application/json'),
    secret: '',
  })
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }))
  const save = useMutation({
    mutationFn: () => {
      const body: IntegrationInput = { name: f.name, description: f.description }
      if (f.kind === 'http') {
        const headers: Record<string, string> = {}
        for (const line of f.headers.split('\n')) {
          const i = line.indexOf(':')
          if (i > 0) headers[line.slice(0, i).trim()] = line.slice(i + 1).trim()
        }
        body.config = { base_url: f.base_url, auth: f.auth, ...(f.auth_name && { auth_name: f.auth_name }), ...(f.username && { username: f.username }), headers, ...(f.test_path && { test_path: f.test_path }), ...(f.test_body && { test_body: f.test_body }), body_content_type: f.body_content_type }
      } else if (!initial) {
        body.config = {}
      }
      if (f.secret) body.secret = f.secret
      if (initial) return api.patchIntegration(initial.slug, body)
      return api.createIntegration({ ...body, slug: f.slug || slugify(f.name), kind: f.kind })
    },
    onSuccess: onDone,
  })
  const needsSecret = f.kind === 'google_drive' || f.auth !== 'none'

  return (
    <form
      className="card form"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <h2>{initial ? `Edit ${initial.name}` : 'New integration'}</h2>
      <div className="form-grid">
        <label>Name<input value={f.name} onChange={(e) => set('name')(e.target.value)} required /></label>
        {!initial && (
          <>
            <label>Slug<input value={f.slug} placeholder={slugify(f.name)} onChange={(e) => set('slug')(e.target.value)} /></label>
            <label>Kind
              <select value={f.kind} onChange={(e) => set('kind')(e.target.value)}>
                <option value="http">HTTP API</option>
                <option value="google_drive">Google Drive (service account)</option>
              </select>
            </label>
          </>
        )}
        {f.kind === 'http' && (
          <>
            <label className="wide">Base URL (sources pick a path under it; credentials are only sent here)
              <input value={f.base_url} onChange={(e) => set('base_url')(e.target.value)} required />
            </label>
            <label>Auth
              <select value={f.auth} onChange={(e) => set('auth')(e.target.value)}>
                <option value="none">none</option>
                <option value="bearer">Bearer token</option>
                <option value="basic">Basic (username + password)</option>
                <option value="header">API key header</option>
                <option value="query">API key query parameter</option>
                <option value="body">API key in the POST body ({'{{secret}}'})</option>
              </select>
            </label>
            {(f.auth === 'header' || f.auth === 'query') && (
              <label>{f.auth === 'header' ? 'Header name' : 'Parameter name'}<input value={f.auth_name} onChange={(e) => set('auth_name')(e.target.value)} required /></label>
            )}
            {f.auth === 'basic' && <label>Username<input value={f.username} onChange={(e) => set('username')(e.target.value)} required /></label>}
            <label className="wide">Extra headers (one per line, Name: value)<textarea rows={2} value={f.headers} onChange={(e) => set('headers')(e.target.value)} /></label>
            <label>Test path (optional)<input value={f.test_path} placeholder="/status" onChange={(e) => set('test_path')(e.target.value)} /></label>
            <label>POST body content type<input className="mono" value={f.body_content_type} placeholder="text/xml" onChange={(e) => set('body_content_type')(e.target.value)} /></label>
            {f.auth === 'body' && (
              <label className="wide">Test body (posted by “test connection”; {'{{secret}}'} is replaced by the key, escaped for the content type)
                <textarea rows={3} className="mono" value={f.test_body} spellCheck={false} onChange={(e) => set('test_body')(e.target.value)} required />
              </label>
            )}
          </>
        )}
        {needsSecret && (
          <label className="wide">
            {f.kind === 'google_drive' ? 'Service-account key (JSON)' : 'Secret'}
            {initial?.secret_hint && <span className="muted"> · stored: {initial.secret_hint}; leave empty to keep it</span>}
            {f.kind === 'google_drive' ? (
              <textarea rows={4} className="mono" value={f.secret} autoComplete="off" spellCheck={false} onChange={(e) => set('secret')(e.target.value)} />
            ) : (
              <input type="password" value={f.secret} autoComplete="new-password" onChange={(e) => set('secret')(e.target.value)} />
            )}
          </label>
        )}
        <label className="wide">Description<textarea rows={2} value={f.description} onChange={(e) => set('description')(e.target.value)} /></label>
      </div>
      {save.error && <ErrorBox error={save.error} />}
      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        {!initial && <span className="note">New integrations start disabled; enable one after testing it.</span>}
      </div>
    </form>
  )
}
